import { useSyncExternalStore } from 'react';
import { IDIOMAS_SITE } from './idiomaMoedaSite';
import { definirMoeda, moedaDoIdioma } from './moedaSite';

// IDIOMA DO SITE — API do seletor que mora DENTRO DO MENU de cada modelo + `?lang=` na URL (22/10/2026).
//
// - Um widget do GTranslate por página (o do cabeçalho do topo 3, ou um ESCONDIDO montado no _app —
//   components/pieces/idiomas/IdiomaOculto). Os itens de idioma dos menus são botões nossos que chamam
//   `trocarIdioma(lang)`: aciona o widget (clique na opção escondida; reserva = `doGTranslate`), põe o
//   preço na moeda do idioma (definirMoeda/moedaDoIdioma) e grava `?lang=xx` na URL.
// - URL: ISO 639-1 de 2 letras (`?lang=pt|en|es|fr|it|de`, o padrão do hreflang). Na ENTRADA aceita
//   também 3 letras (por, eng, spa, fra/fre, ita, deu/ger) e região (en-US) e normaliza para 2. A troca
//   usa `history.replaceState` (preserva os outros parâmetros e o hash, e o estado do router do Next);
//   navegando pelo site, o `lang` volta à URL a cada `routeChangeComplete` enquanto o idioma não for o
//   português. Abrir um link com `?lang=en` já abre traduzido e com a moeda do idioma.
// - Tudo no navegador e só nos sites com o seletor (quem chama já está atrás de temSeletorIdiomas):
//   SSR, canonical e SEO não mudam (o canonical já descarta a query — utils/url.cleanPath).
// - O idioma em vigor do tradutor fica no localStorage `__GT_TRANSLATE_LANGS` ({srcLang, tgtLang}),
//   que a lib do GTranslate lê ao carregar e apaga ao voltar para o português. Gravar ali ANTES de o
//   widget carregar é o que faz o link com `?lang=` abrir já traduzido.
// - Não reage ao `lang` do <html> (o tradutor alterna sozinho ao reprocessar — ver utils/moedaSite).

export type IdiomaSite = (typeof IDIOMAS_SITE)[number];

/** Nome de cada idioma nele mesmo (como o widget mostra com `native_language_names`). */
export const NOME_IDIOMA: Record<IdiomaSite, string> = {
    pt: 'Português', en: 'English', es: 'Español', fr: 'Français', it: 'Italiano', de: 'Deutsch',
};

/** Bandeiras do mesmo CDN do widget (pt = Brasil, en = EUA, como o `alt_flags` do SeletorIdiomas). */
export const BANDEIRA_IDIOMA: Record<IdiomaSite, string> = {
    pt: 'https://cdn.gtranslate.net/flags/svg/pt-br.svg',
    en: 'https://cdn.gtranslate.net/flags/svg/en-us.svg',
    es: 'https://cdn.gtranslate.net/flags/svg/es.svg',
    fr: 'https://cdn.gtranslate.net/flags/svg/fr.svg',
    it: 'https://cdn.gtranslate.net/flags/svg/it.svg',
    de: 'https://cdn.gtranslate.net/flags/svg/de.svg',
};

const TRES_LETRAS: Record<string, IdiomaSite> = {
    por: 'pt', eng: 'en', spa: 'es', fra: 'fr', fre: 'fr', ita: 'it', deu: 'de', ger: 'de',
};

/** "en", "EN", "eng", "en-US", "pt_BR" → código de 2 letras suportado; o resto → null. */
export function normalizarIdioma(valor: unknown): IdiomaSite | null {
    const base = String(valor ?? '').trim().toLowerCase().split(/[-_]/)[0];
    if ((IDIOMAS_SITE as readonly string[]).includes(base)) return base as IdiomaSite;
    return TRES_LETRAS[base] ?? null;
}

const CHAVE_TRADUTOR = '__GT_TRANSLATE_LANGS';
// Opções do widget (a caixa `.gt_selected` não tem `data-gt-lang`).
const SELETOR_OPCAO = '[class*="gtranslate_wrapper"] .gt_option a[data-gt-lang]';
const seletorDaOpcao = (lang: IdiomaSite) => `[class*="gtranslate_wrapper"] .gt_option a[data-gt-lang="${lang}"]`;

let escolhido: IdiomaSite | null = null; // lido sob demanda (só no navegador)
let pendente: IdiomaSite | null = null; // escolha que espera o widget recarregar
let instalado = false;
const ouvintes = new Set<() => void>();

const avisar = () => ouvintes.forEach((f) => f());

/** Idioma que o tradutor vai aplicar/está aplicando (localStorage da lib), ou português. */
function idiomaDoTradutor(): IdiomaSite {
    try {
        const salvo = JSON.parse(window.localStorage.getItem(CHAVE_TRADUTOR) || 'null');
        return normalizarIdioma(salvo?.tgtLang) ?? 'pt';
    } catch {
        return 'pt';
    }
}

function gravarIdiomaDoTradutor(lang: IdiomaSite) {
    try {
        if (lang === 'pt') window.localStorage.removeItem(CHAVE_TRADUTOR);
        else window.localStorage.setItem(CHAVE_TRADUTOR, JSON.stringify({ srcLang: 'pt', tgtLang: lang }));
    } catch { /* modo privado: o widget ainda troca nesta página */ }
}

/** Valor bruto de `lang` na query da página (o primeiro). */
const langBrutoDaUrl = (): string | null => {
    const m = window.location.search.match(/[?&]lang=([^&#]*)/i);
    if (!m) return null;
    try { return decodeURIComponent(m[1].replace(/\+/g, ' ')); } catch { return m[1]; }
};

/** `?lang=` da URL atual, normalizado (null se ausente ou não suportado). */
export const idiomaDaUrl = (): IdiomaSite | null => {
    const bruto = langBrutoDaUrl();
    return bruto === null ? null : normalizarIdioma(bruto);
};

/** Idioma escolhido pelo visitante (URL > tradutor > português). No servidor, sempre português. */
export function idiomaAtual(): IdiomaSite {
    if (typeof window === 'undefined') return 'pt';
    if (escolhido === null) escolhido = idiomaDaUrl() ?? idiomaDoTradutor();
    return escolhido;
}

/**
 * Grava `lang=<2 letras>` na URL sem navegar: troca o valor no lugar (ou acrescenta no fim), mantém os
 * outros parâmetros como estão (sem re-encodar) e o hash. O estado do router do Next (`__N`, `as`) vai
 * junto, para o voltar/avançar do navegador continuar funcionando.
 */
export function escreverIdiomaNaUrl(lang: IdiomaSite) {
    if (typeof window === 'undefined') return;
    const { pathname, search, hash } = window.location;
    const partes = search.replace(/^\?/, '').split('&').filter(Boolean);
    let achou = false;
    const novas: string[] = [];
    partes.forEach((p) => {
        if (!/^lang(=|$)/i.test(p)) { novas.push(p); return; }
        if (!achou) novas.push(`lang=${lang}`); // repetido: fica um só
        achou = true;
    });
    if (!achou) novas.push(`lang=${lang}`);
    const novoSearch = `?${novas.join('&')}`;
    if (novoSearch === search) return;
    const destino = `${pathname}${novoSearch}${hash}`;
    try {
        const estado = window.history.state;
        const novoEstado = estado && typeof estado === 'object' && 'as' in estado ? { ...estado, as: destino } : estado;
        window.history.replaceState(novoEstado, '', destino);
    } catch { /* navegador sem history: a URL fica como estava */ }
}

const definirEscolhido = (lang: IdiomaSite) => {
    if (escolhido === lang) return;
    escolhido = lang;
    avisar();
};

/** Aciona o tradutor: opção do widget (atualiza a caixa dele também) ou, sem widget, a função global. */
function acionarTradutor(lang: IdiomaSite) {
    const opcao = document.querySelector<HTMLElement>(seletorDaOpcao(lang));
    if (opcao) {
        opcao.click(); // o widget chama doGTranslate; o ouvinte de utils/moedaSite também vê o clique
        return;
    }
    gravarIdiomaDoTradutor(lang); // widget ainda não carregou: o dwf.js lê daqui ao carregar
    const w = window as any;
    if (typeof w.doGTranslate === 'function') w.doGTranslate(`pt|${lang}`);
}

/**
 * Troca o idioma do site (itens de idioma dos menus). Moeda do idioma + `?lang=` + tradutor.
 * Sem efeito com o mesmo idioma já escolhido.
 */
export function trocarIdioma(valor: string) {
    const lang = normalizarIdioma(valor);
    if (!lang || typeof window === 'undefined') return;
    if (lang === idiomaAtual() && lang === idiomaDoTradutor()) {
        escreverIdiomaNaUrl(lang);
        return;
    }
    definirEscolhido(lang);
    definirMoeda(moedaDoIdioma(lang));
    escreverIdiomaNaUrl(lang);
    acionarTradutor(lang);
}

/**
 * Instala, uma vez, o ouvinte do clique direto no widget visível (cabeçalho do topo 3): a escolha feita
 * ali também vai para a URL e para o destaque dos menus. A moeda desse clique é do utils/moedaSite.
 */
export function instalarIdiomaSite() {
    if (instalado || typeof document === 'undefined') return;
    instalado = true;
    document.addEventListener('click', (e) => {
        if (!e.isTrusted) return; // o clique que o trocarIdioma dispara já cuidou de tudo
        const opcao = (e.target as Element | null)?.closest?.(SELETOR_OPCAO);
        const lang = normalizarIdioma(opcao?.getAttribute('data-gt-lang'));
        if (!lang) return;
        definirEscolhido(lang);
        escreverIdiomaNaUrl(lang);
    }, true);
}

/**
 * Chamado pelo SeletorIdiomas ANTES de injetar o script do widget. Com `?lang=` na URL, ele manda: grava
 * o idioma onde a lib do tradutor lê ao carregar (página já abre traduzida) e põe a moeda do idioma —
 * só quando o idioma MUDA (uma moeda escolhida à mão no 2504 sobrevive à navegação). Sem `?lang=` e com
 * a página num idioma estrangeiro, devolve o `lang` à URL.
 */
export function prepararIdiomaDaUrl() {
    if (typeof window === 'undefined') return;
    const daUrl = idiomaDaUrl();
    if (!daUrl) {
        const atual = idiomaAtual();
        if (atual !== 'pt') escreverIdiomaNaUrl(atual);
        return;
    }
    const doTradutor = idiomaDoTradutor();
    definirEscolhido(daUrl);
    escreverIdiomaNaUrl(daUrl); // normaliza "eng"/"EN"/"en-US" para "en"
    if (daUrl === doTradutor) return;
    definirMoeda(moedaDoIdioma(daUrl));
    gravarIdiomaDoTradutor(daUrl);
    // A lib do tradutor só lê o localStorage na 1ª carga; já carregada (navegação), aplica no onload.
    if ((window as any).gt_translate_script) pendente = daUrl;
}

/** `onload` do script do widget: aplica a escolha que esperava a lib já carregada. */
export function widgetIdiomaCarregou() {
    if (!pendente) return;
    const lang = pendente;
    pendente = null;
    const w = window as any;
    if (typeof w.doGTranslate === 'function') w.doGTranslate(`pt|${lang}`);
}

/** `routeChangeComplete` do Next: o `lang` volta à URL nova enquanto o idioma não for o português. */
export function manterIdiomaNaUrl() {
    const atual = idiomaAtual();
    if (atual !== 'pt') escreverIdiomaNaUrl(atual);
}

const assinar = (f: () => void) => {
    ouvintes.add(f);
    return () => { ouvintes.delete(f); };
};
const lerServidor = (): IdiomaSite => 'pt';

/** Idioma escolhido, para destacar a opção ativa nos menus (português no SSR e na hidratação). */
export const useIdiomaSite = (): IdiomaSite => useSyncExternalStore(assinar, idiomaAtual, lerServidor);
